/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import {WorkBook} from 'xlsx';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AccountLink,
  BulkActionType,
  Entity
} from '@ion/core/src/types/entities';
import {BulkUploadDialog} from '@ion/dls/src/imports/BulkUploadDialog';
import {formatErrorMessage} from '@ion/dls/src/utils/ComponentUtils';
import useCarbImport from '@ion/query/src/hooks/useCarbImport';
import useS3BucketUrl from '@ion/query/src/hooks/useS3BucketUrl';
import useAuthenticatedUser from '../users/useAuthenticatedUser';

// defines the prop definitions for import devices container ...
export interface ImportCarbAssetsContainerProps {
  account?: AccountLink;
  noTemplate?: boolean;
  setShowCarbImport?: Function;
  onComplete: (newMessage: string) => void;
};

// defines the override styles ...
const overrides = () => ({
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    maxHeight   : '574px',
    height      : '574px'
  }
});

/**
 * This component acts as a container to initiate bulk CARB Data import ...
 */
export const ImportCarbAssetsContainer = ({
  setShowCarbImport,
  onComplete,
  account
}: ImportCarbAssetsContainerProps) => {
  const accountId                               = entityId(account);
  const {id: loggedInUserId}                    = useAuthenticatedUser();
  const [bulkImportParams, setBulkImportParams] = useState<WorkBook | Blob>(null);
  const {uploadFile, isUploadingFile}           = useS3BucketUrl();
  const {importCarb, isProcessing}              = useCarbImport();
  const entityLabel                             = translate('common:entityLabels.carbAssets');
  const customInstructions                      = translate('common:editors.import.carbInstructions',
    {entity: entityLabel});
  
  const onClose = useCallback(() => {
    setShowCarbImport?.(false);
  }, []);

  const importCarbData = (s3Key) => {
    importCarb({
      entityIdentifierType   : Entity.vin,
      accountId              : accountId,
      requestType            : Entity.carbAsset,
      taskNickName           : translate('carb:import.taskName'),
      entityIdentifierS3Path : s3Key
    }, {
      onSuccess : () => {
        const msg = (
          <>
            <div>{translate('carb:import.successMsg1')}</div>
            <div>{translate('carb:import.successMsg2')}</div>
            <div>{translate('carb:import.successMsg3')}</div>
          </>
        );
        
        onComplete(msg);
        onClose();
      },
      onError : (err) => {
        onComplete(formatErrorMessage(err) || translate('carb:import.failed'));
        error(translate('carb:import.error'), err);
      }
    });
  };

  const handleSubmit = useCallback(() => {
    uploadFile({
      actionType : BulkActionType.COMMON_FIELD_UPDATE,
      workbook   : bulkImportParams,
      entityType : Entity.carbAsset,
      userId     : loggedInUserId,
      accountId
    }, {
      onSuccess : (s3DownloadKey) => {
        importCarbData(s3DownloadKey);
      },
      onError : (err) => {
        error(translate('carb:import.uploadFailed'), err);
      }
    });
  }, [bulkImportParams, loggedInUserId, onClose, translate]);

  const title = useMemo(() => {
    return translate('common:editors.import.title', {
      entity : 'CARB Data'
    });
  }, []);

  return (<>
    <BulkUploadDialog open
      isValidFile
      omitTemplateLinks
      customInstructions={customInstructions}
      dialogTitle={title}
      onClose={onClose}
      onCancel={onClose}
      styleOverrides={overrides}
      onImport={handleSubmit}
      onConfirm={onClose}
      entity={Entity.carbAsset}
      entityLabel={translate('common:entityLabels.carbAssets')}
      isLoading={isUploadingFile || isProcessing}
      isDisabled={false}
      omitActions={false}
      setBulkImportParams={setBulkImportParams} />
  </>
  );
};

export default ImportCarbAssetsContainer;
